// Pure, side-effect-free helpers for available-stock display and validation,
// shared by the reservations and dispatches forms (both show the same number
// per product + bodega). Kept separate from the components so they can be
// unit-tested without mounting React or mocking HTTP layers.
//
// This module is the single source of truth for the availability types and
// helpers; `privatePages/Reservations/types.ts` re-exports the types for the
// existing import sites.

/** One row of per-product+location available stock — returned by
 *  GET /v2/reservations/available-stock and GET /v2/dispatches/available-stock */
export interface AvailableStockItem {
  product_id: number;
  storage_address_id: number;
  physical_stock: number;
  reserved_stock: number;
  available_stock: number;
}

/** Response envelope for the availability endpoints */
export interface AvailableStockResponse {
  items: AvailableStockItem[];
}

/** Single insufficient-stock entry returned in a 422 error's details.insufficient_stock */
export interface InsufficientStockEntry {
  product_id: number;
  storage_address_id: number;
  requested: number;
  available: number;
}

/** A form line item shape used by the validation helpers. */
export interface StockRow {
  product_id: number;
  storage_address_id: number;
  quantity: number;
}

/** Per-row error map keyed by the item's index in the form's items array. */
export type StockErrorMap = Record<number, string>;

/**
 * Builds the `items` query-string value for the availability endpoints.
 * Only rows where BOTH product and address have been selected contribute a pair.
 * Returns null when no row qualifies, so callers can disable the fetch.
 *
 * Examples:
 *   []                                     -> null
 *   [{product_id:0, address_id:0, qty:0}]  -> null
 *   [{10,2,5},{20,3,4}]                     -> "10:2,20:3"
 */
export function buildAvailableStockQuery(
  items: ReadonlyArray<StockRow>
): string | null {
  const pairs = items
    .filter((i) => i.product_id > 0 && i.storage_address_id > 0)
    .map((i) => `${i.product_id}:${i.storage_address_id}`);
  return pairs.length > 0 ? pairs.join(",") : null;
}

/**
 * Indexes the backend's AvailableStockItem[] into a fast lookup keyed by
 * "product_id:storage_address_id" -> available_stock.
 */
export function indexAvailableStock(
  items: AvailableStockItem[]
): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of items) {
    map.set(`${item.product_id}:${item.storage_address_id}`, item.available_stock);
  }
  return map;
}

/**
 * Client-side validation: compares every filled row against the available
 * stock map. A row whose quantity exceeds the known available stock gets an
 * entry in the returned error map; rows without stock info or incomplete rows
 * are skipped (no error until we have something to compare against).
 */
export function validateQuantities(
  items: ReadonlyArray<StockRow>,
  available: Map<string, number>
): StockErrorMap {
  const errors: StockErrorMap = {};
  items.forEach((row, idx) => {
    // Only validate fully-filled rows; empty rows are covered by the form's
    // own "items valid" gate, not by stock validation.
    if (row.product_id <= 0 || row.storage_address_id <= 0 || row.quantity <= 0) {
      return;
    }
    const stock = available.get(`${row.product_id}:${row.storage_address_id}`);
    if (stock === undefined) return; // no stock info yet → defer to backend
    if (row.quantity > stock) {
      errors[idx] = `La cantidad excede el stock disponible (${stock})`;
    }
  });
  return errors;
}

/**
 * Maps a backend 422 response whose `details.insufficient_stock` lists the
 * offending product+address pairs back to per-row inline errors, matching each
 * entry against the current form items by product_id + storage_address_id.
 * Returns an empty map when the error has no insufficient_stock detail (so
 * other error paths — e.g. quote_number field errors — are unaffected).
 */
export function mapInsufficientStockError(
  error: any,
  items: ReadonlyArray<StockRow>
): StockErrorMap {
  const errors: StockErrorMap = {};
  const insufficient: InsufficientStockEntry[] | undefined =
    error?.response?.data?.details?.insufficient_stock;
  if (!Array.isArray(insufficient)) return errors;

  items.forEach((row, idx) => {
    const match = insufficient.find(
      (entry) =>
        entry.product_id === row.product_id &&
        entry.storage_address_id === row.storage_address_id
    );
    if (match) {
      errors[idx] = `La cantidad excede el stock disponible (${match.available})`;
    }
  });
  return errors;
}
